import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import FlashcardViewer from '../components/FlashcardViewer';
import './ModuleViewer.css';

const ModuleViewer = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const { moduleCode } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQuizzes, setShowQuizzes] = useState(false);  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResults, setQuizResults] = useState({});
  const [submittingQuiz, setSubmittingQuiz] = useState(null);
  const [quizAttempts, setQuizAttempts] = useState([]);
  const [progressRecordId, setProgressRecordId] = useState(null);
  const [quizScore, setQuizScore] = useState(null);
  const [meetsThreshold, setMeetsThreshold] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  
  // Study Notes state (LEARN-1)
  const [studyNotes, setStudyNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [newNoteSection, setNewNoteSection] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [editNoteSection, setEditNoteSection] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(false);
  
  // Share Notes state (COLLAB-1)
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingNoteId, setSharingNoteId] = useState(null);
  const [allPMs, setAllPMs] = useState([]);
  const [selectedPM, setSelectedPM] = useState('');
  const [sharePermission, setSharePermission] = useState('view');
  const [noteShares, setNoteShares] = useState({});
  const [loadingShares, setLoadingShares] = useState(false);
  
  const userEmail = localStorage.getItem('userEmail') || 'pm@awnex.com';

  useEffect(() => {
    fetchModuleContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleCode]);

  const fetchModuleContent = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch module by module_code
      const res = await fetch(`${API_URL}/training/modules/code/${moduleCode}`, {
        headers: {
          'x-user-email': userEmail
        }
      });
      
      if (!res.ok) {
        throw new Error('Module not found');
      }
      
      const data = await res.json();
      setModule(data.data);
      setQuizzes(data.quizzes || []);

      // Fetch progress record and quiz attempts
      if (data.data && data.data.id) {
        try {
          let progressRes = await fetch(`${API_URL}/training/progress/module/${data.data.id}`, {
            headers: {
              'x-user-email': userEmail
            }
          });

          // If no progress record exists, create one automatically
          if (progressRes.status === 404) {
            console.log('No progress record found, creating one...');
            
            // First get the PM ID
            const pmRes = await fetch(`${API_URL}/training/pms`, {
              headers: {
                'x-user-email': userEmail
              }
            });
            
            if (pmRes.ok) {
              const pmData = await pmRes.json();
              console.log('All PMs:', pmData.data);
              const currentPM = pmData.data.find(pm => pm.email === userEmail);
              console.log('Current user email:', userEmail);
              console.log('Found PM:', currentPM);
              
              if (currentPM) {
                // Create progress record
                const createPayload = {
                  pmId: currentPM.id,
                  moduleId: data.data.id,
                  dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
                  totalSteps: data.quizzes?.length || 1
                };
                console.log('Creating progress record with:', createPayload);
                
                const createRes = await fetch(`${API_URL}/training/progress`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'x-user-email': userEmail
                  },
                  body: JSON.stringify(createPayload)
                });
                
                if (createRes.ok) {
                  const createData = await createRes.json();
                  console.log('Progress record created successfully:', createData);
                  // Fetch the newly created progress record
                  progressRes = await fetch(`${API_URL}/training/progress/module/${data.data.id}`, {
                    headers: {
                      'x-user-email': userEmail
                    }
                  });
                } else {
                  const errorData = await createRes.json();
                  console.error('Failed to create progress record:', createRes.status, errorData);
                }
              } else {
                console.error('No PM found with email:', userEmail);
                console.log('Available emails:', pmData.data.map(pm => pm.email));
              }
            } else {
              console.error('Failed to fetch PMs:', pmRes.status);
            }
          }

          if (progressRes.ok) {
            const progressData = await progressRes.json();
            const progId = progressData.data?.id;
            setProgressRecordId(progId);

            // Fetch quiz attempts and score
            if (progId) {
              const attemptsRes = await fetch(`${API_URL}/training/quizzes/attempts/${progId}`, {
                headers: {
                  'x-user-email': userEmail
                }
              });

              if (attemptsRes.ok) {
                const attemptsData = await attemptsRes.json();
                setQuizAttempts(attemptsData.data || []);
              }

              // Fetch quiz score to check 80% threshold
              const scoreRes = await fetch(`${API_URL}/training/quizzes/score/${progId}`, {
                headers: {
                  'x-user-email': userEmail
                }
              });

              if (scoreRes.ok) {
                const scoreData = await scoreRes.json();
                setQuizScore(scoreData.data);
                setMeetsThreshold(scoreData.data.meets_threshold);
              }
            }
          }
        } catch (err) {
          console.error('Error fetching progress/attempts:', err);
        }

        // Fetch related documents
        fetchModuleDocuments(data.data.id);
        
        // Fetch study notes
        fetchStudyNotes(data.data.id);
      }
    } catch (err) {
      console.error('Error fetching module:', err);
      setError(err.message || 'Failed to load module');
    } finally {
      setLoading(false);
    }
  };

  const fetchModuleDocuments = async (moduleId) => {
    try {
      setLoadingDocuments(true);
      const response = await fetch(`${API_URL}/documents/module/${moduleId}`);
      const data = await response.json();
      
      if (data.success) {
        setDocuments(data.data);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Study Notes Functions (LEARN-1)
  const fetchStudyNotes = async (moduleId) => {
    try {
      setLoadingNotes(true);
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/learning/notes/${moduleId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setStudyNotes(data.notes);
      }
    } catch (err) {
      console.error('Error fetching study notes:', err);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleCreateNote = async () => {
    if (!newNoteText.trim()) return;
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/learning/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          moduleId: module.id,
          noteText: newNoteText,
          sectionReference: newNoteSection
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setStudyNotes([data.note, ...studyNotes]);
        setNewNoteText('');
        setNewNoteSection('');
      }
    } catch (err) {
      console.error('Error creating note:', err);
    }
  };

  const handleUpdateNote = async (noteId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const note = studyNotes.find(n => n.id === noteId);
      const response = await fetch(`${API_URL}/learning/notes/${noteId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          noteText: editNoteText,
          sectionReference: editNoteSection,
          isPinned: note.is_pinned
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setStudyNotes(studyNotes.map(n => n.id === noteId ? data.note : n));
        setEditingNoteId(null);
        setEditNoteText('');
        setEditNoteSection('');
      }
    } catch (err) {
      console.error('Error updating note:', err);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/learning/notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setStudyNotes(studyNotes.filter(n => n.id !== noteId));
      }
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  };

  const handleTogglePin = async (noteId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const note = studyNotes.find(n => n.id === noteId);
      const response = await fetch(`${API_URL}/learning/notes/${noteId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          noteText: note.note_text,
          sectionReference: note.section_reference,
          isPinned: !note.is_pinned
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setStudyNotes(studyNotes.map(n => n.id === noteId ? data.note : n));
      }
    } catch (err) {
      console.error('Error toggling pin:', err);
    }
  };

  // Fetch all PMs for sharing dropdown
  const fetchAllPMs = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/training/pms`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success) {
        setAllPMs(data.pms);
      }
    } catch (err) {
      console.error('Error fetching PMs:', err);
    }
  };

  // Fetch current shares for a note
  const fetchNoteShares = async (noteId) => {
    try {
      setLoadingShares(true);
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/notes/${noteId}/shares`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success) {
        setNoteShares({...noteShares, [noteId]: data.shares});
      }
    } catch (err) {
      console.error('Error fetching note shares:', err);
    } finally {
      setLoadingShares(false);
    }
  };

  // Share Notes Functions (COLLAB-1)
  const handleShareNote = (noteId) => {
    setSharingNoteId(noteId);
    setShowShareModal(true);
    setSelectedPM('');
    setSharePermission('view');
    if (allPMs.length === 0) fetchAllPMs();
    fetchNoteShares(noteId);
  };
  
  const handleShareSubmit = async () => {
    if (!selectedPM || !sharingNoteId) return;
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/notes/${sharingNoteId}/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sharedWithPmId: parseInt(selectedPM),
          permissions: sharePermission
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to share note');
      }
      
      alert('Note shared successfully!');
      fetchNoteShares(sharingNoteId);
      setSelectedPM('');
    } catch (err) {
      console.error('Error sharing note:', err);
      alert(err.message || 'Failed to share note. Please try again.');
    }
  };
  
  const handleUnshare = async (noteId, pmId) => {
    if (!window.confirm('Remove sharing access for this PM?')) return;
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/notes/${noteId}/share/${pmId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) throw new Error('Failed to unshare note');
      
      fetchNoteShares(noteId);
      alert('Sharing access removed.');
    } catch (err) {
      console.error('Error unsharing note:', err);
      alert('Failed to remove sharing access.');
    }
  };

  const handleDownloadDocument = (docId, fileName) => {
    const token = localStorage.getItem('accessToken');
    const email = localStorage.getItem('userEmail');
    const headers = new Headers();
    
    if (token) {
      headers.append('Authorization', `Bearer ${token}`);
    } else if (email) {
      headers.append('x-user-email', email);
    }

    fetch(`${API_URL}/documents/${docId}/download`, { headers })
      .then(response => {
        if (!response.ok) throw new Error('Download failed');
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch(err => {
        console.error('Error downloading document:', err);
        alert('Failed to download document');
      });
  };

  const handleCompleteModule = async () => {
    if (!module || !progressRecordId) {
      alert('Progress record not found. Please try refreshing the page.');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/training/progress/${progressRecordId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-user-email': userEmail
        },
        body: JSON.stringify({
          status: 'Completed',
          completedDate: new Date().toISOString().split('T')[0],
          completedSteps: 1
        })
      });
      
      if (res.ok) {
        alert('Module marked as complete! 🎉');
        navigate('/dashboard');
      } else {
        const errorData = await res.json();
        console.error('Failed to complete module:', errorData);
        alert(`Failed to mark module as complete: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error completing module:', err);
      alert('Error marking module complete');
    }
  };

  const handleQuizAnswerChange = (quizId, answer) => {
    setQuizAnswers(prev => ({
      ...prev,
      [quizId]: answer
    }));
  };

  const handleSubmitQuiz = async (quiz) => {
    const userAnswer = quizAnswers[quiz.quiz_id]?.trim();
    
    if (!userAnswer) {
      alert('Please provide an answer before submitting.');
      return;
    }

    setSubmittingQuiz(quiz.quiz_id);

    try {
      let isCorrect = false;
      let correctAnswer = quiz.answer_key || quiz.correct_answer || '';
      
      // Handle Multiple Choice validation
      if (quiz.question_type === 'Multiple Choice' && quiz.correct_option) {
        // For MC, check if user selected the correct option letter
        isCorrect = userAnswer.toUpperCase() === quiz.correct_option.toUpperCase();
        // Show the full option text as correct answer
        const optionKey = `option_${quiz.correct_option.toLowerCase()}`;
        correctAnswer = `${quiz.correct_option}. ${quiz[optionKey] || quiz.answer_key}`;
      } else if (quiz.question_type === 'True/False') {
        // For True/False, check exact match (case insensitive)
        const correctAnswerLower = correctAnswer.toLowerCase();
        const userAnswerLower = userAnswer.toLowerCase();
        isCorrect = userAnswerLower === correctAnswerLower || 
                   (correctAnswerLower.includes('true') && userAnswerLower.includes('true')) ||
                   (correctAnswerLower.includes('false') && userAnswerLower.includes('false'));
      } else {
        // For Short Answer, use flexible keyword matching
        const correctAnswerLower = correctAnswer.toLowerCase();
        const userAnswerLower = userAnswer.toLowerCase();
        
        // Extract key concepts from correct answer (words longer than 3 chars)
        const keyWords = correctAnswerLower.split(/\W+/)
          .filter(word => word.length > 3 && !['this', 'that', 'with', 'from', 'have', 'been', 'their', 'should', 'would', 'could'].includes(word));
        
        // Check if user answer contains at least 50% of key concepts
        if (keyWords.length > 0) {
          const matchedWords = keyWords.filter(word => userAnswerLower.includes(word));
          const matchPercentage = (matchedWords.length / keyWords.length) * 100;
          isCorrect = matchPercentage >= 50;
        } else {
          // Fallback to basic substring matching
          isCorrect = userAnswerLower.includes(correctAnswerLower) ||
                     correctAnswerLower.includes(userAnswerLower);
        }
      }
      
      const score = isCorrect ? 100 : 0;

      // Store result locally for immediate feedback
      setQuizResults(prev => ({
        ...prev,
        [quiz.quiz_id]: {
          submitted: true,
          correct: isCorrect,
          score: score,
          userAnswer: userAnswer,
          correctAnswer: correctAnswer
        }
      }));

      // Submit to backend for tracking
      if (progressRecordId) {
        const submitRes = await fetch(`${API_URL}/training/quizzes/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-user-email': userEmail
          },
          body: JSON.stringify({
            progressRecordId,
            quizId: quiz.quiz_id,
            pmAnswer: userAnswer,
            score,
            passed: isCorrect
          })
        });

        if (submitRes.ok) {
          // Refresh quiz attempts and score to show new submission
          const attemptsRes = await fetch(`${API_URL}/training/quizzes/attempts/${progressRecordId}`, {
            headers: {
              'x-user-email': userEmail
            }
          });

          if (attemptsRes.ok) {
            const attemptsData = await attemptsRes.json();
            setQuizAttempts(attemptsData.data || []);
          }

          // Refresh quiz score
          const scoreRes = await fetch(`${API_URL}/training/quizzes/score/${progressRecordId}`, {
            headers: {
              'x-user-email': userEmail
            }
          });

          if (scoreRes.ok) {
            const scoreData = await scoreRes.json();
            setQuizScore(scoreData.data);
            setMeetsThreshold(scoreData.data.meets_threshold);
          }
        } else {
          console.error('Error submitting quiz to backend');
        }
      }

    } catch (error) {
      console.error('Error submitting quiz:', error);
      alert('Error submitting quiz');
    } finally {
      setSubmittingQuiz(null);
    }
  };

  if (loading) {
    return (
      <div className="module-viewer-container">
        <div className="loading-spinner">Loading module content...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="module-viewer-container">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="module-viewer-container">
        <div className="error-message">
          <h2>Module Not Found</h2>
          <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="module-viewer-container">
      <div className="module-header">
        <button className="back-button" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <div className="module-meta">
          <span className={`module-badge ${module.certification_track?.toLowerCase()}`}>
            {module.certification_track}
          </span>
          <span className="module-code">{module.module_code}</span>
        </div>
      </div>

      <div className="module-content-wrapper">
        <div className="module-title-section">
          <h1>{module.title || module.module_name}</h1>
          {module.learning_objectives && (
            <div className="learning-objectives">
              <h3>🎯 Learning Objectives</h3>
              <p>{module.learning_objectives}</p>
            </div>
          )}
          {module.prerequisites && (
            <div className="prerequisites">
              <strong>Prerequisites:</strong> {module.prerequisites}
            </div>
          )}
        </div>

        <div className="module-content">
          {module.content ? (
            <ReactMarkdown>{module.content}</ReactMarkdown>
          ) : (
            <div className="no-content">
              <p>📚 Module content coming soon!</p>
              <p>This module is part of the {module.certification_track} track.</p>
            </div>
          )}
        </div>

        {/* Related Documents Section */}
        {documents.length > 0 && (
          <div className="related-documents-section">
            <h2>📄 Related Documents</h2>
            <div className="documents-grid">
              {documents.map(doc => (
                <div key={doc.id} className="document-card">
                  <div className="document-header">
                    <span className={`doc-type-badge ${doc.document_type.toLowerCase()}`}>
                      {doc.document_type}
                    </span>
                    <span className="doc-version">v{doc.version_number}</span>
                  </div>
                  <h4 className="doc-title">{doc.title}</h4>
                  {doc.sop_code && (
                    <div className="doc-sop-code">{doc.sop_code}</div>
                  )}
                  {doc.description && (
                    <p className="doc-description">{doc.description}</p>
                  )}
                  <div className="document-footer">
                    <span className="doc-size">
                      {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : 'N/A'}
                    </span>
                    <button
                      className="download-doc-btn"
                      onClick={() => handleDownloadDocument(doc.id, doc.file_name)}
                      title="Download document"
                    >
                      ⬇️ Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Study Notes Section (LEARN-1) */}
        <div className="study-notes-section">
          <h2>📝 Study Notes</h2>
          
          <div className="create-note-form">
            <textarea
              placeholder="Add your notes about this module..."
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              rows="4"
              className="note-textarea"
            />
            <div className="note-form-footer">
              <input
                type="text"
                placeholder="Section reference (optional)"
                value={newNoteSection}
                onChange={(e) => setNewNoteSection(e.target.value)}
                className="note-section-input"
              />
              <button 
                onClick={handleCreateNote}
                disabled={!newNoteText.trim()}
                className="create-note-btn"
              >
                ➕ Add Note
              </button>
            </div>
          </div>

          {loadingNotes ? (
            <div className="loading-notes">Loading notes...</div>
          ) : studyNotes.length > 0 ? (
            <div className="notes-list">
              {studyNotes.map(note => (
                <div key={note.id} className={`note-card ${note.is_pinned ? 'pinned' : ''}`}>
                  {editingNoteId === note.id ? (
                    <div className="edit-note-form">
                      <textarea
                        value={editNoteText}
                        onChange={(e) => setEditNoteText(e.target.value)}
                        rows="4"
                        className="note-textarea"
                      />
                      <input
                        type="text"
                        placeholder="Section reference"
                        value={editNoteSection}
                        onChange={(e) => setEditNoteSection(e.target.value)}
                        className="note-section-input"
                      />
                      <div className="edit-actions">
                        <button 
                          onClick={() => handleUpdateNote(note.id)}
                          className="save-note-btn"
                        >
                          💾 Save
                        </button>
                        <button 
                          onClick={() => {
                            setEditingNoteId(null);
                            setEditNoteText('');
                            setEditNoteSection('');
                          }}
                          className="cancel-btn"
                        >
                          ✖ Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="note-header">
                        {note.is_pinned && <span className="pin-badge">📌 Pinned</span>}
                        {note.section_reference && (
                          <span className="section-badge">{note.section_reference}</span>
                        )}
                        <div className="note-actions">
                          <button
                            onClick={() => handleTogglePin(note.id)}
                            className="icon-btn"
                            title={note.is_pinned ? 'Unpin' : 'Pin note'}
                          >
                            {note.is_pinned ? '📌' : '📍'}
                          </button>
                          <button
                            onClick={() => handleShareNote(note.id)}
                            className="icon-btn"
                            title="Share note with another PM"
                          >
                            🔗
                          </button>
                          <button
                            onClick={() => {
                              setEditingNoteId(note.id);
                              setEditNoteText(note.note_text);
                              setEditNoteSection(note.section_reference || '');
                            }}
                            className="icon-btn"
                            title="Edit note"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            className="icon-btn delete"
                            title="Delete note"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      <div className="note-text">{note.note_text}</div>
                      <div className="note-footer">
                        <span className="note-date">
                          {new Date(note.created_at).toLocaleDateString()}
                          {note.updated_at !== note.created_at && ' (edited)'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-notes">
              No notes yet. Add your first note above to help you remember key concepts!
            </div>
          )}
        </div>

        {/* Flashcard Viewer (LEARN-2) */}
        {module && <FlashcardViewer moduleId={module.id} />}
        
        {/* Share Note Modal (COLLAB-1) */}
        {showShareModal && (
          <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Share Study Note</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowShareModal(false)}
              >
                ✖
              </button>
              
              <div className="modal-body">
                <div className="form-group">
                  <label>Share with PM:</label>
                  <select 
                    value={selectedPM}
                    onChange={(e) => setSelectedPM(e.target.value)}
                    className="form-select"
                  >
                    <option value="">-- Select PM --</option>
                    {allPMs.map(pm => (
                      <option key={pm.id} value={pm.id}>
                        {pm.full_name} ({pm.email})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Permissions:</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input 
                        type="radio" 
                        value="view"
                        checked={sharePermission === 'view'}
                        onChange={(e) => setSharePermission(e.target.value)}
                      />
                      <span>View only</span>
                    </label>
                    <label className="radio-label">
                      <input 
                        type="radio" 
                        value="edit"
                        checked={sharePermission === 'edit'}
                        onChange={(e) => setSharePermission(e.target.value)}
                      />
                      <span>Can edit</span>
                    </label>
                  </div>
                </div>
                
                <button 
                  onClick={handleShareSubmit}
                  disabled={!selectedPM}
                  className="share-submit-btn"
                >
                  Share Note
                </button>
                
                {noteShares[sharingNoteId] && noteShares[sharingNoteId].length > 0 && (
                  <div className="current-shares">
                    <h4>Currently shared with:</h4>
                    <ul className="shares-list">
                      {noteShares[sharingNoteId].map(share => (
                        <li key={share.id}>
                          <span className="share-name">{share.shared_with_name}</span>
                          <span className="share-permission">({share.permissions})</span>
                          <button 
                            onClick={() => handleUnshare(sharingNoteId, share.shared_with_pm_id)}
                            className="unshare-btn"
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {quizzes.length > 0 && (
          <div className="quiz-section">
            <div className="quiz-header">
              <h2>📝 Knowledge Check</h2>
              <button 
                className="toggle-quiz-btn"
                onClick={() => setShowQuizzes(!showQuizzes)}
              >
                {showQuizzes ? 'Hide' : 'Show'} {quizzes.length} {quizzes.length === 1 ? 'Question' : 'Questions'}
              </button>
            </div>
            
            {showQuizzes && (
              <div className="quiz-list">
                {quizzes.map((quiz, index) => {
                  const result = quizResults[quiz.quiz_id];
                  const isSubmitted = result?.submitted;
                  
                  return (
                    <div key={quiz.quiz_id} className={`quiz-card ${isSubmitted ? (result.correct ? 'correct' : 'incorrect') : ''}`}>
                      <div className="quiz-number">Question {index + 1}</div>
                      <div className={`quiz-difficulty ${quiz.difficulty?.toLowerCase()}`}>
                        {quiz.difficulty}
                      </div>
                      <h3>{quiz.question}</h3>
                      <div className="quiz-type">{quiz.question_type}</div>
                      
                      {!isSubmitted ? (
                        <div className="quiz-input-section">
                          {quiz.question_type === 'Multiple Choice' && quiz.option_a ? (
                            // Render radio buttons for Multiple Choice
                            <div className="quiz-options">
                              {['A', 'B', 'C', 'D'].map(letter => {
                                const optionKey = `option_${letter.toLowerCase()}`;
                                const optionText = quiz[optionKey];
                                if (!optionText) return null;
                                
                                return (
                                  <label key={letter} className="quiz-option">
                                    <input
                                      type="radio"
                                      name={`quiz-${quiz.quiz_id}`}
                                      value={letter}
                                      checked={quizAnswers[quiz.quiz_id] === letter}
                                      onChange={(e) => handleQuizAnswerChange(quiz.quiz_id, e.target.value)}
                                    />
                                    <span className="option-label">{letter}.</span>
                                    <span className="option-text">{optionText}</span>
                                  </label>
                                );
                              })}
                            </div>
                          ) : (
                            // Render text input for other question types
                            <textarea
                              className="quiz-answer-input"
                              placeholder="Enter your answer here..."
                              value={quizAnswers[quiz.quiz_id] || ''}
                              onChange={(e) => handleQuizAnswerChange(quiz.quiz_id, e.target.value)}
                              rows={3}
                            />
                          )}
                          <button
                            className="submit-quiz-btn"
                            onClick={() => handleSubmitQuiz(quiz)}
                            disabled={submittingQuiz === quiz.quiz_id || !quizAnswers[quiz.quiz_id]?.trim()}
                          >
                            {submittingQuiz === quiz.quiz_id ? 'Submitting...' : 'Submit Answer'}
                          </button>
                        </div>
                      ) : (
                        <div className="quiz-result">
                          <div className={`result-badge ${result.correct ? 'correct' : 'incorrect'}`}>
                            {result.correct ? '✓ Correct!' : '✗ Incorrect'}
                          </div>
                          <div className="user-answer">
                            <strong>Your Answer:</strong>{' '}
                            {quiz.question_type === 'Multiple Choice' && quiz.correct_option ? (
                              <>
                                {result.userAnswer}.{' '}
                                {quiz[`option_${result.userAnswer.toLowerCase()}`] || result.userAnswer}
                              </>
                            ) : (
                              result.userAnswer
                            )}
                          </div>
                          {!result.correct && (
                            <div className="correct-answer">
                              <strong>Correct Answer:</strong> {result.correctAnswer}
                            </div>
                          )}
                          <button 
                            className="retry-btn"
                            onClick={() => {
                              setQuizResults(prev => {
                                const updated = {...prev};
                                delete updated[quiz.quiz_id];
                                return updated;
                              });
                              setQuizAnswers(prev => {
                                const updated = {...prev};
                                delete updated[quiz.quiz_id];
                                return updated;
                              });
                            }}
                          >
                            Try Again
                          </button>
                        </div>
                      )}
                      
                      {quiz.tags && (
                        <div className="quiz-tags">
                          {quiz.tags.split(',').map(tag => (
                            <span key={tag} className="tag">{tag.trim()}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {quizAttempts.length > 0 && (
          <div className="quiz-history-section">
            <h2>📊 Quiz Attempt History</h2>
            
            {quizScore && (
              <div className={`quiz-score-banner ${meetsThreshold ? 'passed' : 'failed'}`}>
                <div className="score-display">
                  <div className="score-value">{quizScore.score}%</div>
                  <div className="score-label">Overall Quiz Score</div>
                </div>
                <div className="score-info">
                  <p><strong>Attempts:</strong> {quizScore.total_attempts} | <strong>Passed:</strong> {quizScore.passed_attempts}</p>
                  <p className={`threshold-status ${meetsThreshold ? 'meets' : 'needs'}`}>
                    {meetsThreshold 
                      ? `✓ Meets 80% requirement - You can complete this module!`
                      : `✗ Below 80% requirement - Keep practicing to unlock module completion`
                    }
                  </p>
                </div>
              </div>
            )}
            
            <div className="quiz-attempts-list">
              {quizAttempts.map((attempt) => (
                <div key={attempt.id} className={`attempt-card ${attempt.passed ? 'passed' : 'failed'}`}>
                  <div className="attempt-meta">
                    <span className={`status-badge ${attempt.passed ? 'passed' : 'failed'}`}>
                      {attempt.passed ? '✓ Passed' : '✗ Failed'}
                    </span>
                    <span className="attempt-number">Attempt {attempt.attempt_number}</span>
                    <span className="attempt-score">{attempt.score}%</span>
                  </div>
                  <div className="attempt-content">
                    <div className="question-text">
                      <strong>Q:</strong> {attempt.quiz_text}
                    </div>
                    <div className="attempt-answer">
                      <strong>Your Answer:</strong> {attempt.pm_answer}
                    </div>
                    <div className="attempt-timestamp">
                      Submitted: {new Date(attempt.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="module-actions">
          <button 
            className={`complete-module-btn ${!meetsThreshold ? 'disabled' : ''}`}
            onClick={handleCompleteModule}
            disabled={!meetsThreshold}
            title={meetsThreshold ? 'Complete this module' : 'Quiz score must be 80% or higher to complete'}
          >
            ✓ Mark Module Complete
            {!meetsThreshold && quizScore && ` (${quizScore.score}% - Need 80%)`}
          </button>
          <button 
            className="secondary-btn"
            onClick={() => navigate('/dashboard')}
          >
            Save Progress & Exit
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModuleViewer;
